import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  Company,
  CompanyListItem,
  CompanyCreateRequest,
  CompanyUpdateRequest,
  PagedResult,
  SalesforceCompany,
} from '../../core';
import {
  CompaniesPageActions,
  CompanyDetailPageActions,
  CompanyFormActions,
} from './companies.actions';
import * as CompaniesSelectors from './companies.selectors';

@Injectable({ providedIn: 'root' })
export class CompaniesFacade {
  private readonly store = inject(Store);

  // List observables
  readonly companies$: Observable<PagedResult<CompanyListItem> | null> =
    this.store.select(CompaniesSelectors.selectCompanies);
  readonly companyItems$: Observable<CompanyListItem[]> = this.store.select(
    CompaniesSelectors.selectCompanyItems
  );
  readonly loading$: Observable<boolean> = this.store.select(
    CompaniesSelectors.selectCompaniesLoading
  );
  readonly error$: Observable<string | null> = this.store.select(
    CompaniesSelectors.selectCompaniesError
  );
  readonly currentPage$: Observable<number> = this.store.select(
    CompaniesSelectors.selectCurrentPage
  );
  readonly pageSize$: Observable<number> = this.store.select(
    CompaniesSelectors.selectPageSize
  );
  readonly totalCount$: Observable<number> = this.store.select(
    CompaniesSelectors.selectTotalCount
  );
  readonly totalPages$: Observable<number> = this.store.select(
    CompaniesSelectors.selectTotalPages
  );
  readonly searchTerm$: Observable<string> = this.store.select(
    CompaniesSelectors.selectSearchTerm
  );
  readonly sortBy$: Observable<string> = this.store.select(
    CompaniesSelectors.selectSortBy
  );
  readonly sortOrder$: Observable<'asc' | 'desc' | ''> = this.store.select(
    CompaniesSelectors.selectSortOrder
  );

  // Detail observables
  readonly currentCompany$: Observable<Company | null> = this.store.select(
    CompaniesSelectors.selectCurrentCompany
  );
  readonly currentCompanyLoading$: Observable<boolean> = this.store.select(
    CompaniesSelectors.selectCurrentCompanyLoading
  );
  readonly currentCompanyError$: Observable<string | null> = this.store.select(
    CompaniesSelectors.selectCurrentCompanyError
  );

  // Form observables
  readonly saving$: Observable<boolean> = this.store.select(
    CompaniesSelectors.selectSaving
  );
  readonly saveSuccess$: Observable<boolean> = this.store.select(
    CompaniesSelectors.selectSaveSuccess
  );
  readonly saveError$: Observable<string | null> = this.store.select(
    CompaniesSelectors.selectSaveError
  );
  readonly arNumberExists$: Observable<boolean | null> = this.store.select(
    CompaniesSelectors.selectARNumberExists
  );
  readonly salesforceResults$: Observable<SalesforceCompany[]> =
    this.store.select(CompaniesSelectors.selectSalesforceResults);
  readonly salesforceLoading$: Observable<boolean> = this.store.select(
    CompaniesSelectors.selectSalesforceLoading
  );

  // Actions
  loadCompanies(page?: number, pageSize?: number): void {
    this.store.dispatch(CompaniesPageActions.loadCompanies({ page, pageSize }));
  }

  changePage(page: number): void {
    this.store.dispatch(CompaniesPageActions.changePage({ page }));
  }

  changePageSize(pageSize: number): void {
    this.store.dispatch(CompaniesPageActions.changePageSize({ pageSize }));
  }

  searchCompanies(search: string): void {
    this.store.dispatch(CompaniesPageActions.searchCompanies({ search }));
  }

  sort(field: string): void {
    this.store.dispatch(CompaniesPageActions.sort({ field }));
  }

  loadCompany(arNumber: string): void {
    this.store.dispatch(CompanyDetailPageActions.loadCompany({ arNumber }));
  }

  clearCurrentCompany(): void {
    this.store.dispatch(CompanyDetailPageActions.clearCurrentCompany());
  }

  createCompany(request: CompanyCreateRequest): void {
    this.store.dispatch(CompanyFormActions.createCompany({ request }));
  }

  updateCompany(arNumber: string, request: CompanyUpdateRequest): void {
    this.store.dispatch(
      CompanyFormActions.updateCompany({ arNumber, request })
    );
  }

  checkARNumberExists(arNumber: string): void {
    this.store.dispatch(CompanyFormActions.checkARNumberExists({ arNumber }));
  }

  searchSalesforce(query: string): void {
    this.store.dispatch(CompanyFormActions.searchSalesforce({ query }));
  }
}
